import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import Newsletter from "../components/Newsletter";
import { useSEO } from "../lib/useSEO";
import { HERO_IMG, SILHOUETTE_IMG } from "../lib/images";

const EASE = [0.22, 1, 0.36, 1];

function Hero() {
  const { site } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const lines = [site.hero_line_1, site.hero_line_2].filter(Boolean);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(212,175,55,0.08),transparent_55%)]" />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 pb-24 pt-20 lg:grid-cols-12 lg:px-12 lg:pb-40 lg:pt-32">
        <motion.div style={{ y: yText }} className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-gold"
            data-testid="hero-tagline"
          >
            {site.tagline}
          </motion.p>
          <h1 className="mt-8 font-serif text-5xl leading-[1.05] text-zinc-100 sm:text-7xl lg:text-8xl" data-testid="hero-title">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.35 + i * 0.18, ease: EASE }}
                  className={`block ${i === lines.length - 1 ? "gold-text-gradient" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-lg text-base leading-relaxed text-zinc-400"
            data-testid="hero-subtitle"
          >
            {site.hero_subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/reflexoes" className="btn-gold" data-testid="hero-cta-reflexoes">
              Explorar reflexões <ArrowRight size={14} />
            </Link>
            <Link to="/comunidade" className="btn-ghost-dark" data-testid="hero-cta-comunidade">
              Entrar na comunidade
            </Link>
          </motion.div>
        </motion.div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
            className="hairline-frame relative aspect-[3/4] overflow-hidden border border-gold/20"
          >
            <motion.img
              src={HERO_IMG}
              alt="Arte abstrata em dourado"
              style={{ y: yImg, scale: 1.15 }}
              className="h-full w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="absolute -bottom-8 -left-12 border border-gold/25 bg-black/70 px-8 py-6 backdrop-blur-xl"
          >
            <p className="font-serif text-3xl text-gold" data-testid="hero-symbol">✦</p>
            <p className="mt-2 text-[0.65rem] uppercase tracking-[0.3em] text-zinc-400">Para quem quer<br />sair do automático</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MarqueeRibbon() {
  const { site } = useSite();
  const items = site.marquee_text.split("✦").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="border-y border-gold/15 bg-[#080808] py-5" data-testid="marquee-ribbon">
      <Marquee speed={35} gradient={false}>
        {items.concat(items).map((item, i) => (
          <span key={i} className="mx-10 flex items-center gap-10 font-serif text-sm uppercase tracking-[0.35em] text-zinc-500">
            {item} <span className="text-gold">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Manifesto() {
  const { site } = useSite();
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-40" data-testid="manifesto-section">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">O Manifesto</p>
        <h2 className="mt-6 max-w-2xl font-serif text-4xl text-zinc-100 sm:text-5xl lg:text-6xl">
          Três pilares. Um despertar.
        </h2>
      </Reveal>
      <div className="mt-20 grid gap-px bg-gold/10 lg:grid-cols-3">
        {site.manifesto.map((chapter, i) => (
          <Reveal key={i} delay={i * 0.15} className="bg-[#050505]">
            <div className="group flex h-full flex-col p-10 transition-colors duration-500 hover:bg-[#0a0a0a] lg:p-12" data-testid={`manifesto-chapter-${i + 1}`}>
              <span className="font-serif text-6xl text-gold/25 transition-colors duration-500 group-hover:text-gold/60 lg:text-7xl">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-8 font-serif text-2xl tracking-[0.15em] text-zinc-100">{chapter.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{chapter.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function About() {
  const { site } = useSite();
  return (
    <section className="border-t border-gold/15" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-40">
        <Reveal className="lg:col-span-5">
          <div className="hairline-frame relative aspect-[4/5] overflow-hidden border border-gold/20">
            <img src={SILHOUETTE_IMG} alt="Silhueta em cena urbana noturna" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
          </div>
        </Reveal>
        <div className="flex flex-col justify-center lg:col-span-7 lg:pl-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">A REFLLEXIVE</p>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-zinc-100 sm:text-5xl">{site.about_title}</h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400">{site.about_text}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturedPosts() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (posts.length === 0) return null;
  return (
    <section className="border-t border-gold/15 bg-[#080808]" data-testid="featured-posts-section">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Reflexões</p>
            <h2 className="mt-6 font-serif text-4xl text-zinc-100 sm:text-5xl">Últimos despertares</h2>
          </div>
          <Link to="/reflexoes" className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-transform hover:-translate-y-1 sm:flex" data-testid="see-all-posts-link">
            Ver todas <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <div className="mt-16 grid gap-8 md:grid-cols-3">
          {posts.map((post, i) => (
            <Reveal key={post.id} delay={i * 0.12}>
              <Link to={`/reflexoes/${post.slug}`} className="group block" data-testid={`featured-post-${post.slug}`}>
                <div className="relative aspect-[4/3] overflow-hidden border border-gold/15">
                  {post.cover_url && (
                    <img src={post.cover_url} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-zinc-100 transition-colors group-hover:text-gold">{post.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-500">{post.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  if (products.length === 0) return null;
  return (
    <section className="border-t border-gold/15" data-testid="featured-products-section">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Produtos</p>
            <h2 className="mt-6 font-serif text-4xl text-zinc-100 sm:text-5xl">Ferramentas de evolução</h2>
          </div>
          <Link to="/produtos" className="hidden items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-transform hover:-translate-y-1 sm:flex" data-testid="see-all-products-link">
            Ver todos <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <div className="mt-16 grid gap-8 md:grid-cols-3">
          {products.map((product, i) => (
            <Reveal key={product.id} delay={i * 0.12}>
              <a href={product.checkout_url} target="_blank" rel="noreferrer" className="group block border border-gold/15 bg-[#0a0a0a] transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_0_30px_rgba(212,175,55,0.1)]" data-testid={`featured-product-${i}`}>
                <div className="relative aspect-[16/10] overflow-hidden">
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                </div>
                <div className="p-8">
                  <h3 className="font-serif text-xl text-zinc-100">{product.name}</h3>
                  <p className="mt-4 font-serif text-lg text-gold">{product.price_label}</p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CommunityTeaser() {
  const { site } = useSite();
  return (
    <section className="relative overflow-hidden border-t border-gold/15 bg-[#0a0a0a]" data-testid="community-teaser-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(212,175,55,0.07),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-40">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Mentoria & Comunidade</p>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl leading-tight text-zinc-100 sm:text-5xl lg:text-6xl">{site.community_title}</h2>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400">{site.community_text}</p>
          <div className="mt-10">
            <Link to="/comunidade" className="btn-gold" data-testid="community-cta-button">
              Acessar área de membros <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO();
  return (
    <>
      <Hero />
      <MarqueeRibbon />
      <Manifesto />
      <About />
      <FeaturedPosts />
      <FeaturedProducts />
      <CommunityTeaser />
      <Newsletter />
    </>
  );
}
